import React, { useCallback, useEffect, useRef, useState } from "react"

// Mini-carrousel de carte (suite, loft, service) : toutes les photos d'un même
// lieu dans le cadre de la carte, sans vignettes.
//   ① défilement horizontal AIMANTÉ (scroll-snap) : balayage natif au doigt ;
//   ② points + compteur « 2 / 6 » discrets sur l'image ;
//   ③ flèches translucides visibles au survol (souris uniquement) et au clavier ;
//   ④ toutes les images en lazy-load, cadre à ratio fixe (aucun saut de mise en page).
//
// images: [{ src, alt, fit? }]  (fit: "contain" pour un plan d'étage)
// Une seule image : rendu simple, sans commandes.

const STYLE = `
  .mgy { position:relative; width:100%; overflow:hidden; background:#f5f5f7; }
  .mgy-track { display:flex; width:100%; height:100%; overflow-x:auto; overflow-y:hidden; scroll-snap-type:x mandatory; overscroll-behavior-x:contain; -webkit-overflow-scrolling:touch; scrollbar-width:none; outline:none; }
  .mgy-track::-webkit-scrollbar { display:none; }
  .mgy-track:focus-visible { box-shadow:inset 0 0 0 3px #0071e3; }
  .mgy-slide { flex:0 0 100%; width:100%; height:100%; scroll-snap-align:start; scroll-snap-stop:always; }
  .mgy-count { position:absolute; top:12px; right:12px; padding:4px 10px; border-radius:999px; background:rgba(0,0,0,0.42); backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); color:#fff; font-size:12px; font-weight:500; letter-spacing:0.02em; font-variant-numeric:tabular-nums; pointer-events:none; }
  .mgy-dots { position:absolute; left:0; right:0; bottom:12px; display:flex; justify-content:center; gap:6px; pointer-events:none; }
  .mgy-dot { width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,0.5); box-shadow:0 0 4px rgba(0,0,0,0.25); transition:background 240ms, transform 240ms; }
  .mgy-dot[data-on="1"] { background:#fff; transform:scale(1.35); }
  .mgy-arrow { position:absolute; top:50%; width:36px; height:36px; margin-top:-18px; border:0; border-radius:50%; background:rgba(255,255,255,0.86); color:#1d1d1f; box-shadow:0 4px 14px rgba(0,0,0,0.18); display:flex; align-items:center; justify-content:center; cursor:pointer; opacity:0; transition:opacity 220ms, transform 220ms; }
  .mgy-arrow:hover { transform:scale(1.06); }
  .mgy-arrow:focus-visible { opacity:1; outline:3px solid #0071e3; outline-offset:2px; }
  .mgy-prev { left:10px; }
  .mgy-next { right:10px; }
  @media (hover:hover) and (pointer:fine) { .mgy:hover .mgy-arrow { opacity:1; } }
  @media (hover:none), (pointer:coarse) { .mgy-arrow { display:none; } }
  @media (prefers-reduced-motion: reduce) { .mgy-dot, .mgy-arrow { transition:none; } }
`

const MiniGallery = ({ images = [], locale = "fr", aspect = "3 / 2", label = "" }) => {
  const trackRef = useRef(null)
  const [active, setActive] = useState(0)
  const n = images.length
  const t = locale === "en"
    ? { prev: "Previous photo", next: "Next photo", of: "of", photos: "Photos" }
    : { prev: "Photo précédente", next: "Photo suivante", of: "sur", photos: "Photos" }

  const measure = useCallback(() => {
    const el = trackRef.current
    if (!el || !el.clientWidth) return
    setActive(Math.max(0, Math.min(n - 1, Math.round(el.scrollLeft / el.clientWidth))))
  }, [n])

  useEffect(() => {
    const el = trackRef.current
    if (!el || n < 2) return undefined
    let raf = 0
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; measure() }) }
    el.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      el.removeEventListener("scroll", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [measure, n])

  const go = i => {
    const el = trackRef.current
    if (!el) return
    const target = (i + n) % n
    const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    el.scrollTo({ left: target * el.clientWidth, behavior: reduced ? "auto" : "smooth" })
  }

  const onKeyDown = e => {
    if (e.key === "ArrowRight") { e.preventDefault(); go(active + 1) }
    else if (e.key === "ArrowLeft") { e.preventDefault(); go(active - 1) }
  }

  if (!n) return null

  const img = it => (
    <img
      src={it.src}
      alt={it.alt}
      width={1600}
      height={1067}
      loading="lazy"
      decoding="async"
      draggable={false}
      style={{ display: "block", width: "100%", height: "100%", objectFit: it.fit || "cover", background: it.fit === "contain" ? "#f5f5f7" : undefined }}
    />
  )

  if (n === 1) {
    return <div className="mgy" style={{ aspectRatio: aspect }}><style>{STYLE}</style>{img(images[0])}</div>
  }

  const arrow = dir => (
    <button type="button" className={`mgy-arrow ${dir < 0 ? "mgy-prev" : "mgy-next"}`} onClick={() => go(active + dir)} aria-label={dir < 0 ? t.prev : t.next}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {dir < 0 ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
      </svg>
    </button>
  )

  return (
    <div className="mgy" style={{ aspectRatio: aspect }} role="region" aria-roledescription="carousel" aria-label={label ? `${t.photos} — ${label}` : t.photos}>
      <style>{STYLE}</style>
      <div className="mgy-track" ref={trackRef} tabIndex={0} onKeyDown={onKeyDown}>
        {images.map((it, i) => (
          <div key={it.src} className="mgy-slide" role="group" aria-roledescription="slide" aria-label={`${i + 1} ${t.of} ${n}`}>
            {img(it)}
          </div>
        ))}
      </div>
      <div className="mgy-count" aria-hidden="true">{active + 1} / {n}</div>
      <div className="mgy-dots" aria-hidden="true">
        {images.map((it, i) => <span key={it.src} className="mgy-dot" data-on={i === active ? "1" : "0"} />)}
      </div>
      {arrow(-1)}
      {arrow(1)}
    </div>
  )
}

export default MiniGallery
